import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { trackCTAClick } from '../services/analytics'
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Image,
  Icon,
} from '@chakra-ui/react'
import { FaSearch, FaCalendarAlt, FaStar, FaHandshake } from 'react-icons/fa'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import SEO from './SEO.jsx'

const FEATURES = [
  {
    icon: FaSearch,
    title: 'Búsqueda inteligente',
    description: 'Encuentra bandas por ubicación y lo que necesitas para el culto o la procesión.',
  },
  {
    icon: FaCalendarAlt,
    title: 'Disponibilidad',
    description: 'Consulta si la agrupación está libre en tus fechas antes de escribir.',
  },
  {
    icon: FaStar,
    title: 'Valoraciones',
    description: 'Lee opiniones de otras entidades y bandas cuando existan reseñas reales.',
  },
  {
    icon: FaHandshake,
    title: 'Conexión directa',
    description: 'Contacta y negocia sin intermediarios. Chat y acuerdo en la misma plataforma.',
  },
]

const STEPS = [
  { n: '1', title: 'Crea el perfil', text: 'Cuenta de hermandad o entidad contratante.' },
  { n: '2', title: 'Publica un evento', text: 'Fecha, lugar y lo que necesitas de la banda.' },
  { n: '3', title: 'Busca y contacta', text: 'Directorio de bandas y chat sin intermediarios.' },
  { n: '4', title: 'Acuerdo', text: 'Negocia y confirma. El resto del trabajo de la banda vive en Gestión.' },
]

const HermandadesLanding = () => {
  const navigate = useNavigate()

  return (
    <Box>
      <SEO
        title="Hermandades y entidades"
        description="Encuentra y contacta bandas para tus cultos y procesiones. Cadencya conecta hermandades y entidades con agrupaciones validadas."
        path="/hermandades"
      />
      <Header />

      <Box bgGradient="linear(to-br, purple.50, white, gold.50)" pt={{ base: 12, md: 16 }} pb={{ base: 14, md: 20 }}>
        <Container maxW="7xl">
          <HStack
            gap={{ base: 10, lg: 16 }}
            align="center"
            flexDirection={{ base: 'column', lg: 'row' }}
          >
            <VStack
              flex={1}
              gap={6}
              align={{ base: 'center', lg: 'flex-start' }}
              textAlign={{ base: 'center', lg: 'left' }}
            >
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '5xl' }}
                fontWeight="800"
                lineHeight="1.1"
                color="gray.800"
              >
                Encuentra la banda para tus cultos
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600" maxW="xl" lineHeight="tall">
                Compara agrupaciones, mira disponibilidad y contacta sin intermediarios.
                Cadencya valida a las bandas para que sepas con quién estás hablando.
              </Text>
              <HStack
                gap={4}
                w={{ base: 'full', sm: 'auto' }}
                flexDirection={{ base: 'column', sm: 'row' }}
              >
                <Button
                  size="lg"
                  px={8}
                  py={7}
                  fontSize="lg"
                  fontWeight="bold"
                  bg="purple.600"
                  color="white"
                  rounded="full"
                  onClick={() => {
                    trackCTAClick('busco_banda', 'hermandades_hero')
                    navigate('/registro?tipo=cofradia')
                  }}
                  w={{ base: 'full', sm: 'auto' }}
                >
                  Busco una banda
                </Button>
                <Button
                  size="lg"
                  px={8}
                  py={7}
                  fontSize="lg"
                  fontWeight="bold"
                  bg="white"
                  color="purple.600"
                  border="2px solid"
                  borderColor="purple.200"
                  rounded="full"
                  onClick={() => {
                    trackCTAClick('comunidad', 'hermandades_hero')
                    navigate('/comunidad')
                  }}
                  w={{ base: 'full', sm: 'auto' }}
                >
                  Ver la comunidad
                </Button>
              </HStack>
              <Button variant="plain" color="purple.700" fontWeight="700" asChild>
                <RouterLink to="/bandas" onClick={() => trackCTAClick('soy_banda', 'hermandades_hero')}>
                  ¿Eres una banda? Cadencya es tu sistema de gestión
                </RouterLink>
              </Button>
            </VStack>
            <Box flex={1} display="flex" justifyContent="center">
              <Image
                src="/capirote registro.png"
                alt=""
                maxW={{ base: '240px', md: '320px' }}
                css={{ filter: 'drop-shadow(0 16px 32px rgba(124, 58, 237, 0.18))' }}
              />
            </Box>
          </HStack>
        </Container>
      </Box>

      <Box bg="white" py={{ base: 16, md: 20 }}>
        <Container maxW="7xl">
          <VStack gap={10}>
            <VStack gap={3} textAlign="center">
              <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" color="gray.800">
                Rápido y sencilllo
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                En pocos minutos habrás creado tu primer evento y
                descubriendo nuevas bandas para tus cultos, contactando con ellas y llegando a acuerdos.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={8} w="full">
              {FEATURES.map((feature) => (
                <VStack key={feature.title} gap={4} align="flex-start" p={2}>
                  <Box
                    w={12}
                    h={12}
                    rounded="xl"
                    bg="purple.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={feature.icon} boxSize={5} color="purple.600" />
                  </Box>
                  <Heading size="sm" color="gray.800">
                    {feature.title}
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    {feature.description}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="4xl">
          <VStack gap={8}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" color="gray.800" textAlign="center">
              Cómo funciona para tu hermandad
            </Heading>
            <VStack align="stretch" gap={5} w="full">
              {STEPS.map((step) => (
                <HStack
                  key={step.n}
                  align="flex-start"
                  gap={4}
                  bg="white"
                  rounded="2xl"
                  p={5}
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <Box
                    minW={8}
                    h={8}
                    rounded="full"
                    bg="purple.600"
                    color="white"
                    fontWeight="800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="sm"
                  >
                    {step.n}
                  </Box>
                  <Box>
                    <Text fontWeight="700" color="gray.800">
                      {step.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {step.text}
                    </Text>
                  </Box>
                </HStack>
              ))}
            </VStack>
            <Button
              size="lg"
              colorPalette="purple"
              rounded="full"
              px={8}
              onClick={() => {
                trackCTAClick('busco_banda', 'hermandades_pasos')
                navigate('/registro?tipo=cofradia')
              }}
            >
              Crear cuenta de hermandad
            </Button>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  )
}

export default HermandadesLanding
